import { FC, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Breadcrumbs, Button, Input } from '@mondra/ui-components'
import { useQuery } from '@apollo/client'
import { IProduct } from './types'
import { getProductVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'
import { GET_ALL_PRODUCTS } from '../../../query/product'
import { EXPORT_PRODUCT_QUERY } from '../../../query/export-product'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'

const Product: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IProduct[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [product, setProduct] = useState<any>(getProductVars)

  const [order, setOrder] = useState([
    {
      id: 'ASC',
    },
  ])

  const { data, loading, error, refetch } = useQuery(GET_ALL_PRODUCTS, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getProductVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.taxonomyGPCBricks.pageInfo.endCursor)
    setStartCursor(data.taxonomyGPCBricks.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/food/product/gpc',
      'taxonomyGPCBricks',
      'taxonomyGPCBrick'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null) {
      if (!loading) {
        setProduct({
          ...filterEnums(tableSearch, getProductVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getProductVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'TaxonomyGPCBricks',
      query: EXPORT_PRODUCT_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[
              {
                path: '/food/product/gpc',
                title: 'GPC (Product) Table',
              },
            ]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Product</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={() => navigate('/food/product/gpc?itemId=new')} className="hidden">
            Add Product
          </Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={product || getProductVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default Product
